import { dashboardErrorMessage } from '../../../../../../lib/apiErrorMessages';
import { getFormTabErrorCounts } from '../../../../../../lib/formTabErrors';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFormState, useWatch, type FieldErrors, type FieldPath } from 'react-hook-form';
import { Button, Form } from '@shigo-ui/ui';
import {
  DashboardPageFormActions,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
} from '@shigo-ui/dashboard-ui';
import { Clock, Image, ListChecks, MapPinned, Pencil, SlidersHorizontal } from 'lucide-react';

import { EditorSectionTabs } from '../../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../../components/forms/useEditorSection';
import { dashboardT } from '../../../../../../lib/messages';
import type { createCampaignsClient } from '../../../api';
import {
  calculateMadBuyLineItemBudget,
  type MadBuyLineItemGeneralForm,
} from '../../../lineItemsModel';
import { createMadBuyLineItemFormSchema } from '../../../lineItemFormSchema';
import type { TargetingClient } from '../../../../../targeting/api';
import { lineItemTargetingConfig } from '../../../../../targeting/config';
import { TargetingForm } from '../../../../../targeting/components/TargetingForm';
import { LineItemTargetingCsvKind } from '../../../lineItemTargetingCsvModel';
import {
  lineItemTargetingCsvFormFields,
  type LineItemTargetingCsvFailure,
  type LineItemTargetingCsvSaveResult,
} from '../../../saveLineItemWithTargetingCsv';
import { useLineItemTargetingCsv } from '../useLineItemTargetingCsv';
import { CustomTargetingExtForm } from '../CustomTargeting';
import { LineItemConversionTrackingFields } from '../LineItemConversionTrackingFields';
import { LineItemCreativesFields } from '../LineItemCreativesFields';
import { LineItemGeneralFields } from '../LineItemGeneralFields';
import { LineItemScheduleFields } from '../LineItemScheduleFields';
import { LineItemEditorTab } from '../LineItemEditorTypes';

type CampaignsClient = ReturnType<typeof createCampaignsClient>;

const lineItemEditorLookupQueryOptions = {
  staleTime: 10 * 60 * 1000,
  retry: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
} as const;

export function LineItemEditorForm({
  brandId,
  campaignId,
  client,
  currency,
  initialForm,
  isEditMode,
  saving,
  preview = false,
  onCancel,
  savedLineItemId,
  savedTargeting,
  targetingClient,
  targetingCsvFailures = {},
  timezoneLabel,
  onSubmit,
}: {
  brandId: string;
  campaignId: string;
  client: CampaignsClient;
  currency: string;
  initialForm: MadBuyLineItemGeneralForm;
  isEditMode: boolean;
  saving: boolean;
  preview?: boolean;
  onCancel?: () => void;
  savedLineItemId?: string | undefined;
  savedTargeting?: Readonly<Record<string, unknown>> | null | undefined;
  targetingClient: TargetingClient;
  targetingCsvFailures?:
    Partial<Record<LineItemTargetingCsvKind, LineItemTargetingCsvFailure>> | undefined;
  timezoneLabel: string;
  /** Resolves with the CSV kinds imported by the save, or `undefined` when nothing was saved. */
  onSubmit: (
    form: MadBuyLineItemGeneralForm,
  ) => Promise<Pick<LineItemTargetingCsvSaveResult, 'imported'> | undefined>;
}) {
  'use no memo';

  const navigate = useNavigate();
  const dateTimeOptions = { timezone: timezoneLabel };
  const methods = useForm<MadBuyLineItemGeneralForm>({
    defaultValues: initialForm,
    resolver: zodResolver(createMadBuyLineItemFormSchema(dateTimeOptions)),
  });
  const form = useWatch({ control: methods.control }) as MadBuyLineItemGeneralForm;
  const { errors: formErrors, submitCount } = useFormState({ control: methods.control });
  const errors = resolveLineItemErrors(formErrors);
  const [editorSection, setActiveTab] = useEditorSectionControls(
    LineItemEditorTab.General,
    Object.values(LineItemEditorTab),
  );
  const canSubmit = !saving && !preview;
  const budget = calculateMadBuyLineItemBudget(form);
  const targetingErrors = resolveTargetingErrors(formErrors.targeting);
  const conversionPixelsQuery = useQuery({
    queryKey: [...(targetingClient.queryScope ?? []), 'mad-buy-conversion-pixels', brandId],
    enabled: Boolean(brandId) && !preview,
    queryFn: () =>
      client.listConversionPixels({
        brandId,
        page: 0,
        size: 1000,
        sortProperty: ['createdOn'],
        sortDirection: 'DESC',
      }),
    ...lineItemEditorLookupQueryOptions,
  });

  const targetingCsvControls = useLineItemTargetingCsv({
    client,
    failures: targetingCsvFailures,
    files: {
      [LineItemTargetingCsvKind.ZipCodes]: form.pendingZipCodesCsv,
      [LineItemTargetingCsvKind.IpAddresses]: form.pendingIpAddressesCsv,
    },
    lineItemId: savedLineItemId,
    onFileChange: (kind, file) => {
      updateField(lineItemTargetingCsvFormFields[kind], file);
    },
    savedTargeting,
  });

  function updateField<TKey extends keyof MadBuyLineItemGeneralForm>(
    key: TKey,
    value: MadBuyLineItemGeneralForm[TKey],
  ) {
    const path = key as FieldPath<MadBuyLineItemGeneralForm>;
    methods.setValue(path, value, {
      shouldDirty: true,
      shouldValidate: submitCount > 0,
    });
  }

  const tabErrors = getFormTabErrorCounts(
    formErrors,
    {
      [LineItemEditorTab.Schedule]: ['startDate', 'endDate', 'dayTimeActivities'],
      [LineItemEditorTab.Targeting]: ['targeting'],
      [LineItemEditorTab.CustomTargeting]: ['customTargetingExt'],
      [LineItemEditorTab.Creatives]: ['lineItemCreatives'],
      [LineItemEditorTab.Conversion]: ['conversionPixelId'],
    },
    LineItemEditorTab.General,
  );

  return (
    <Form {...methods}>
      <form
        noValidate
        onSubmit={(event) =>
          void methods.handleSubmit(
            async (values) => {
              if (!canSubmit) return;
              const result = await onSubmit(values);
              // Imported files are saved; failed ones stay selected for the next save. A file
              // picked while the save ran was never imported, so it stays too.
              for (const kind of result?.imported ?? []) {
                const field = lineItemTargetingCsvFormFields[kind];
                if (methods.getValues(field) === values[field]) methods.setValue(field, null);
              }
            },
            (invalid) => {
              setActiveTab(resolveInvalidLineItemTab(invalid));
            },
          )(event)
        }
      >
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList>
            <DashboardPageFormTab
              value={LineItemEditorTab.General}
              errorCount={tabErrors[LineItemEditorTab.General]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<ListChecks className="h-4 w-4" />}
            >
              {dashboardT('madBuy.lineItems.general')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={LineItemEditorTab.Schedule}
              errorCount={tabErrors[LineItemEditorTab.Schedule]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<Clock className="h-4 w-4" />}
            >
              {dashboardT('madBuy.lineItems.schedule')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={LineItemEditorTab.Targeting}
              errorCount={tabErrors[LineItemEditorTab.Targeting]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<MapPinned className="h-4 w-4" />}
            >
              {dashboardT('madBuy.lineItems.targeting')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={LineItemEditorTab.CustomTargeting}
              errorCount={tabErrors[LineItemEditorTab.CustomTargeting]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<SlidersHorizontal className="h-4 w-4" />}
            >
              {dashboardT('madBuy.lineItems.customTargeting')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={LineItemEditorTab.Creatives}
              errorCount={tabErrors[LineItemEditorTab.Creatives]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<Image className="h-4 w-4" />}
            >
              {dashboardT('madBuy.lineItems.creatives')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={LineItemEditorTab.Conversion}
              errorCount={tabErrors[LineItemEditorTab.Conversion]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<Pencil className="h-4 w-4" />}
            >
              {dashboardT('madBuy.lineItems.conversionTracking')}
            </DashboardPageFormTab>
          </DashboardPageFormTabList>
          <DashboardPageFormTabPanel value={LineItemEditorTab.General}>
            <LineItemGeneralFields
              budget={budget}
              currency={currency}
              errors={errors}
              form={form}
              isEditMode={isEditMode}
              submitted={submitCount > 0}
              updateField={updateField}
            />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={LineItemEditorTab.Schedule}>
            <LineItemScheduleFields
              errors={errors}
              form={form}
              submitted={submitCount > 0}
              timezoneLabel={timezoneLabel}
              updateField={updateField}
            />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={LineItemEditorTab.Targeting}>
            {preview ? (
              <p className="text-muted-foreground p-6 text-sm">
                {dashboardT('agencyWorkspace.unavailable')}
              </p>
            ) : (
              <TargetingForm
                config={lineItemTargetingConfig}
                client={targetingClient}
                csvControls={{
                  zipCodes: targetingCsvControls[LineItemTargetingCsvKind.ZipCodes],
                  ipAddresses: targetingCsvControls[LineItemTargetingCsvKind.IpAddresses],
                }}
                value={form.targeting}
                errors={targetingErrors}
                submitted={submitCount > 0}
                onChange={(targeting) => {
                  updateField('targeting', targeting);
                }}
              />
            )}
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={LineItemEditorTab.CustomTargeting}>
            {preview ? (
              <p className="text-muted-foreground p-6 text-sm">
                {dashboardT('agencyWorkspace.unavailable')}
              </p>
            ) : (
              <CustomTargetingExtForm client={targetingClient} />
            )}
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={LineItemEditorTab.Creatives}>
            {preview ? (
              <p className="text-muted-foreground p-6 text-sm">
                {dashboardT('agencyWorkspace.unavailable')}
              </p>
            ) : (
              <LineItemCreativesFields
                brandId={brandId}
                client={client}
                error={submitCount > 0 ? errors.lineItemCreatives : undefined}
                form={form}
                updateField={updateField}
              />
            )}
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={LineItemEditorTab.Conversion}>
            {conversionPixelsQuery.isError ? (
              <p role="alert" className="text-destructive">
                {dashboardErrorMessage(conversionPixelsQuery.error)}
              </p>
            ) : null}
            <LineItemConversionTrackingFields
              form={form}
              loading={conversionPixelsQuery.isLoading}
              pixels={preview ? [] : (conversionPixelsQuery.data ?? [])}
              updateField={updateField}
            />
          </DashboardPageFormTabPanel>
        </EditorSectionTabs>
        <DashboardPageFormActions>
          <Button
            type="button"
            variant="outline"
            className="rounded-full px-5"
            onClick={() => {
              if (onCancel) onCancel();
              else
                void navigate({
                  to: '/campaigns/campaigns/overview/$campaignId',
                  params: { campaignId },
                });
            }}
          >
            {dashboardT('madBuy.common.cancel')}
          </Button>
          <Button type="submit" className="rounded-full px-5" disabled={!canSubmit}>
            {saving ? dashboardT('madBuy.common.saving') : dashboardT('madBuy.common.save')}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}

function resolveLineItemErrors(errors: FieldErrors<MadBuyLineItemGeneralForm>) {
  const result: Partial<Record<keyof MadBuyLineItemGeneralForm, string>> = {};
  for (const [field, error] of Object.entries(errors)) {
    const message = resolveFieldErrorMessage(error);
    if (message) result[field as keyof MadBuyLineItemGeneralForm] = message;
  }
  const firstTargetingError = Object.values(resolveTargetingErrors(errors.targeting))[0];
  if (firstTargetingError) result.targeting = firstTargetingError;
  return result;
}

function resolveFieldErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  if (typeof record['message'] === 'string') return record['message'];
  for (const [key, nestedError] of Object.entries(record)) {
    if (key === 'ref') continue;
    const message = resolveFieldErrorMessage(nestedError);
    if (message) return message;
  }
  return undefined;
}

function resolveTargetingErrors(error: FieldErrors<MadBuyLineItemGeneralForm>['targeting']) {
  const result: Partial<Record<'ctr' | 'viewability', string>> = {};
  if (error?.ctr?.message) result.ctr = error.ctr.message;
  if (error?.viewability?.message) result.viewability = error.viewability.message;
  return result;
}

function resolveInvalidLineItemTab(errors: FieldErrors<MadBuyLineItemGeneralForm>) {
  if (errors.targeting) return LineItemEditorTab.Targeting;
  if (errors.customTargetingExt) return LineItemEditorTab.CustomTargeting;
  if (errors.lineItemCreatives) return LineItemEditorTab.Creatives;
  if (errors.startDate || errors.endDate || errors.dayTimeActivities)
    return LineItemEditorTab.Schedule;
  return LineItemEditorTab.General;
}
